import React from 'react'
import { GiSpy } from 'react-icons/gi'
import { IoChevronBack } from 'react-icons/io5'
import { useSidebar } from '../context/SidebarContext'
import { useIsMobile } from '../hooks'
import { SidebarNav } from './SidebarNav'
import { LeagueSwitcher } from './LeagueSwitcher'
import { SidebarUser } from './SidebarUser'
import { SearchTrigger } from './SearchModal'
import type { PageId } from './BottomNav'

interface SidebarProps {
  activePage: PageId
  onPageChange: (page: PageId) => void
  onLeagueClick?: (apiLeagueId: number) => void
  onRefresh?: () => Promise<void>
  isRefreshing?: boolean
}

export function Sidebar({ activePage, onPageChange, onLeagueClick, onRefresh, isRefreshing }: SidebarProps) {
  const { isExpanded, toggleExpanded, isMobileMenuOpen, closeMobileMenu } = useSidebar()
  const isMobile = useIsMobile()

  // On mobile, sidebar is always "expanded" in terms of width when open
  const isCollapsed = !isMobile && !isExpanded

  const sidebarClasses = [
    'sidebar',
    isCollapsed ? 'collapsed' : '',
    isMobileMenuOpen ? 'mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`sidebar-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside className={sidebarClasses}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-content">
            <GiSpy style={{ fontSize: 24, color: 'var(--accent)' }} />
            <span className="sidebar-brand-text">StatScout</span>
          </div>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleExpanded}
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <IoChevronBack size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="sidebar-search">
          <SearchTrigger />
        </div>

        {/* League Switcher */}
        <LeagueSwitcher />

        {/* Navigation */}
        <SidebarNav
          activePage={activePage}
          onPageChange={onPageChange}
          onLeagueClick={onLeagueClick}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />

        {/* User */}
        <SidebarUser />
      </aside>
    </>
  )
}
